import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { useAuth } from "@west-tennessee-consulting/ui";
import { logger } from "@/lib/logger";
import type { DomainSearchMessage as EmailSyncDomainSearchMessage } from "@/lib/types/email-sync";

export interface DomainSearchContact {
  email: string;
  name?: string;
  messageCount: number;
  lastMessageDate: string;
  sampleSubject: string;
}

export interface DomainSearchMessage {
  status?: string;
  threadId?: string;
  externalId?: string;
  attachments?: Array<{
    name: string;
    url: string;
    size: number;
    contentType: string;
  }>;
}

export interface DomainSearchResult {
  messages: DomainSearchMessage[];
  contacts: DomainSearchContact[];
  totalMessages: number;
  totalContacts: number;
  domain: string;
  searchDate: string;
}

interface UseDomainSearchOptions {
  enabled?: boolean;
  limit?: number;
  daysBack?: number;
}

const fetchDomainSearch = async (
  domain: string,
  options: UseDomainSearchOptions = {},
  token?: string
): Promise<DomainSearchResult> => {
  if (!domain || typeof domain !== "string" || domain.trim() === "") {
    throw new Error("Domain is required");
  }

  const { limit = 50, daysBack = 30 } = options;

  if (!token) {
    // Fallback: still verify user via supabase if token not provided
    const supabase = getBrowserSupabaseClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not authenticated");
    }
  }

  const searchParams = new URLSearchParams({
    domain: domain.trim(),
    limit: limit.toString(),
    daysBack: daysBack.toString(),
  });

  try {
    const url = `/api/connections/messages/domain-search?${searchParams}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return {
          messages: [],
          contacts: [],
          totalMessages: 0,
          totalContacts: 0,
          domain: domain.trim(),
          searchDate: new Date().toISOString(),
        };
      }
      const errorText = await response.text();
      throw new Error(`Failed to search domain: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error("Error searching domain", error);
    throw error;
  }
};

export const useDomainSearch = (domain: string, options: UseDomainSearchOptions = {}) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  // Create stable query key to prevent unnecessary refetches
  const queryKey = useMemo(
    () => [
      "domainSearch",
      domain,
      {
        limit: options.limit,
        daysBack: options.daysBack,
        enabled: options.enabled,
      },
    ],
    [domain, options.limit, options.daysBack, options.enabled]
  );

  const query = useQuery<DomainSearchResult, Error>({
    queryKey,
    queryFn: () => fetchDomainSearch(domain, options, token || undefined),
    enabled:
      options.enabled !== false && !!domain && typeof domain === "string" && domain.trim() !== "",
    retry: 2, // Retry twice on failure
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for performance
  });

  return query;
};

// Utility function to extract domain from email
export const extractDomainFromEmail = (email: string): string => {
  if (!email || typeof email !== "string") {
    return "";
  }

  const parts = email.trim().split("@");
  if (parts.length !== 2) {
    return "";
  }

  return parts[1].toLowerCase();
};

// Utility function to extract name from email (if available in sender name)
export const extractNameFromEmail = (email: string, senderName?: string): string => {
  if (senderName && senderName !== email) {
    return senderName;
  }

  // Try to extract name from email if it's in format "First Last <email@domain.com>"
  const nameMatch = email.match(/^(.+?)\s*<(.+?)>$/);
  if (nameMatch) {
    return nameMatch[1].trim();
  }

  // Try to extract name from email prefix
  const emailPrefix = email.split("@")[0];
  if (emailPrefix && emailPrefix.includes(".")) {
    return emailPrefix
      .split(".")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  return emailPrefix || "";
};
